/**
 * Who is signed in, and whether they are a platform admin.
 *
 * There is no "am I a platform admin?" route, so this probes the cheapest gated
 * one. The API answers **404**, not 403, to anyone without a `platform_admins` row
 * — the surface is meant to be invisible. That means a 404 here is a clean "no",
 * while any other failure is a real error and must be shown as one: silently
 * reporting an outage as "no access" would send someone hunting for a permissions
 * bug that does not exist.
 *
 * Sessions live in `sessionStorage`, so closing the tab signs out.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Profile } from '@modulus/api'
import { api, clearSession, loadSession, setSessionLostHandler } from './api'

/**
 * How long a signed-in tab stays usable without interaction.
 *
 * Not a security boundary — the token's own expiry is. It is here because this
 * panel is left open on a second monitor for hours, and the actions on it are the
 * ones you least want performed by whoever else sits down.
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000

interface SessionValue {
  loading: boolean
  isSignedIn: boolean
  isPlatformAdmin: boolean
  profile: Profile | null
  /** Set when the check itself failed, as opposed to returning "not an admin". */
  error: string | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  retry: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [isSignedIn, setIsSignedIn] = useState(() => loadSession() !== null)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reset = useCallback(() => {
    clearSession()
    setIsSignedIn(false)
    setIsPlatformAdmin(false)
    setProfile(null)
  }, [])

  useEffect(() => {
    setSessionLostHandler(reset)
    return () => setSessionLostHandler(null)
  }, [reset])

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const [me, admin] = await Promise.all([
          api.learner.profile(),
          api.platform.isPlatformAdmin(),
        ])
        if (cancelled) return
        setProfile(me)
        setIsPlatformAdmin(admin)
      } catch (caught) {
        if (cancelled) return
        // `isPlatformAdmin` already turns 404/403 into `false`, so anything
        // reaching here is a genuine failure — network, or the API being down.
        setError(caught instanceof Error ? caught.message : 'Could not verify access.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isSignedIn, attempt])

  // Idle sign-out.
  const idleTimer = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!isSignedIn) return

    const bump = () => {
      window.clearTimeout(idleTimer.current)
      idleTimer.current = window.setTimeout(() => void api.auth.logout().finally(reset), IDLE_TIMEOUT_MS)
    }

    const events = ['pointerdown', 'keydown', 'visibilitychange'] as const
    events.forEach((event) => window.addEventListener(event, bump, { passive: true }))
    bump()

    return () => {
      events.forEach((event) => window.removeEventListener(event, bump))
      window.clearTimeout(idleTimer.current)
    }
  }, [isSignedIn, reset])

  const value = useMemo<SessionValue>(
    () => ({
      loading,
      isSignedIn,
      isPlatformAdmin,
      profile,
      error,
      signIn: async (email, password) => {
        await api.auth.login(email, password)
        setIsSignedIn(true)
        setAttempt((n) => n + 1)
      },
      signOut: async () => {
        await api.auth.logout()
        reset()
      },
      retry: () => setAttempt((n) => n + 1),
    }),
    [loading, isSignedIn, isPlatformAdmin, profile, error, reset],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside a SessionProvider')
  return value
}
